import { useState } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2, Plus, Eye } from 'lucide-react';
import {
  getSourcingOrders, createSourcingOrder, updateSourcingOrder,
  deleteSourcingOrder, updateSourcingOrderStatus,
} from '../api/sourcingOrders';
import { getSourcers } from '../api/sourcers';
import { getProducts, getProductStructure } from '../api/products';
import { getRawMaterials } from '../api/rawMaterials';
import Modal from '../components/Modal';
import { SourcingOrderForm, EMPTY_FORM, buildSourcingPayload } from '../components/SourcingOrderForm';
import Pagination from '../components/Pagination';
import { useAuth } from '../context/AuthContext';
import { canWrite } from '../utils/permissions';
import { useTranslation } from 'react-i18next';

const NEXT_STATUSES = {
  PENDING:   ['ORDERED', 'CANCELLED'],
  ORDERED:   ['RECEIVED', 'CANCELLED'],
  RECEIVED:  [],
  CANCELLED: [],
};

const STATUS_STYLE = {
  PENDING:   'bg-gray-100 text-gray-600',
  ORDERED:   'bg-blue-50 text-blue-700',
  RECEIVED:  'bg-green-50 text-green-700',
  CANCELLED: 'bg-red-50 text-red-600',
};

function fmt(value) {
  return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatCombo(combo) {
  if (!combo || Object.keys(combo).length === 0) return null;
  return Object.entries(combo).map(([k, v]) => `${k}: ${v}`).join(', ');
}

function StatusBadge({ status }) {
  const { t } = useTranslation();
  return (
    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLE[status]}`}>
      {t(`status.${status}`)}
    </span>
  );
}

function TypeBadge({ type }) {
  const { t } = useTranslation();
  return type === 'RAW_MATERIAL'
    ? <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">{t('sourcingOrders.typeRawMaterial')}</span>
    : <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{t('sourcingOrders.typeProduct')}</span>;
}

function formatDate(value) {
  return new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function DetailModal({ order, sourcers, onClose, onStatusChange, isPending, hideSourcer }) {
  const { t } = useTranslation();
  const next = NEXT_STATUSES[order.status] ?? [];
  const sourcerName = sourcers.find((s) => s.id === order.sourcerId)?.name ?? order.sourcerName;
  const isRm = order.type === 'RAW_MATERIAL';

  return (
    <Modal title={`Sourcing Order #${order.id}`} onClose={onClose} wide>
      <div className="space-y-5">
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            {!hideSourcer && <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{t('sourcers.title')}</p>}
            {order.type && <TypeBadge type={order.type} />}
          </div>
          {!hideSourcer && <p className="font-medium text-gray-800">{sourcerName}</p>}
          {order.orderedAt && <p className="text-xs text-gray-400 mt-1">{t('sourcingOrders.ordered')} {formatDate(order.orderedAt)}</p>}
          {order.receivedAt && <p className="text-xs text-gray-400">{t('sourcingOrders.received')} {formatDate(order.receivedAt)}</p>}
          {order.lastEditedAt && (
            <p className="text-xs text-amber-700 mt-1">
              {t('sourcingOrders.editedBy', { user: order.lastEditedBy, date: formatDate(order.lastEditedAt) })}
            </p>
          )}
        </div>

        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{t('common.items')}</p>
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-left">
                  <th className="px-3 py-2 font-medium text-gray-500">{isRm ? t('rawMaterials.title') : t('common.product')}</th>
                  <th className="px-3 py-2 font-medium text-gray-500 text-right">{t('common.qty')}</th>
                  <th className="px-3 py-2 font-medium text-gray-500 text-right">{t('sourcingOrders.totalCost')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {order.items.map((item) => {
                  const name = isRm ? item.rawMaterialName : item.productTitle;
                  const qty = Number(item.quantity);
                  const combo = formatCombo(item.variantCombo);
                  return (
                    <tr key={item.id}>
                      <td className="px-3 py-2 text-gray-800">
                        {name}
                        {combo && <span className="text-xs text-gray-400 ml-1.5">({combo})</span>}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-600">{qty.toLocaleString('en-US', { maximumFractionDigits: 3 })}</td>
                      <td className="px-3 py-2 text-right font-medium text-gray-800">৳{fmt(Number(item.unitCost) * qty)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                {order.manualDiscount > 0 && (
                  <tr className="border-t border-gray-100">
                    <td colSpan={2} className="px-3 py-2 text-sm text-gray-500 text-right">{t('sourcingOrders.manualDiscount')}</td>
                    <td className="px-3 py-2 text-right text-red-500 font-medium">-৳{fmt(order.manualDiscount)}</td>
                  </tr>
                )}
                <tr className="border-t border-gray-200 bg-gray-50">
                  <td colSpan={2} className="px-3 py-2 text-sm font-semibold text-gray-700 text-right">{t('sourcingOrders.totalCost')}</td>
                  <td className="px-3 py-2 text-right font-bold text-gray-900">৳{fmt(order.totalCost)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {order.notes && <p className="text-sm text-gray-500 bg-gray-50 rounded-lg px-3 py-2"><span className="font-medium text-gray-600">Note:</span> {order.notes}</p>}

        <div className="flex items-center justify-between pt-1 border-t border-gray-100">
          <StatusBadge status={order.status} />
          {next.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">{t('sourcingOrders.moveTo')}</span>
              <div className="flex gap-1">
                {next.map((s) => (
                  <button key={s} onClick={() => onStatusChange(order.id, s)} disabled={isPending}
                    className="px-3 py-1 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-100 text-gray-700 transition-colors disabled:opacity-50">
                    {t(`status.${s}`)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

// A received product stock-in can be corrected (stock is adjusted by the difference on the server);
// received raw-material orders stay locked.
const isReceivedProduct = (order) => order.status === 'RECEIVED' && order.type === 'PRODUCT';
const canEdit = (order) => order.status === 'PENDING' || isReceivedProduct(order);
const canDelete = (order) => !(order.status === 'RECEIVED' && order.type === 'RAW_MATERIAL');

/**
 * Also rendered inside Inventory's "Sourcing history" tab: `embedded` drops the page header and
 * "New" button, `productOnly` lists product stock-ins only, `hideSourcer` hides sourcers (Simple view).
 */
export default function SourcingOrders({ embedded = false, productOnly = false, hideSourcer = false }) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [viewOrder, setViewOrder] = useState(null);
  const [formModal, setFormModal] = useState(null);
  const [formError, setFormError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  usePageTitle(embedded ? undefined : 'Sourcing Orders');
  const { t } = useTranslation();
  const { user } = useAuth();
  const write = canWrite(user?.role);
  const [filters, setFilters] = useState({});
  const handleFilter = (key, val) => { setFilters((f) => ({ ...f, [key]: val || undefined })); setPage(0); };
  const clearFilters = () => { setFilters({}); setPage(0); };
  const listFilters = {
    ...filters,
    ...(productOnly ? { type: 'PRODUCT' } : {}),
    // Inventory's history tab lists newest first; id breaks ties within the same second.
    ...(embedded ? { sort: ['createdAt,desc', 'id,desc'] } : {}),
  };

  const { data, isLoading } = useQuery({
    queryKey: ['sourcing-orders', page, listFilters],
    queryFn: () => getSourcingOrders(page, 10, listFilters).then((r) => r.data.data),
  });

  const { data: sourcers = [] } = useQuery({
    queryKey: ['sourcers-all'],
    queryFn: () => getSourcers(0, 100).then((r) => r.data.data.content),
  });

  const { data: products = [] } = useQuery({
    queryKey: ['products-all'],
    queryFn: () => getProducts(0, 100).then((r) => r.data.data.content),
  });

  const { data: rawMaterialsData } = useQuery({
    queryKey: ['raw-materials-all'],
    queryFn: () => getRawMaterials(0, 200).then((r) => r.data.data),
  });
  const rawMaterials = rawMaterialsData?.content ?? [];

  const { data: globalStructureData } = useQuery({
    queryKey: ['product-structure'],
    queryFn: () => getProductStructure().then((r) => r.data.data),
  });
  const globalFields = globalStructureData?.fields ?? [];

  // Receiving, correcting or deleting a stock-in changes stock, variant stock, cost history and profit.
  const invalidate = () => {
    ['sourcing-orders', 'inventory', 'raw-materials', 'products', 'products-all', 'product-variants',
      'product-cost-history', 'dashboard', 'sold-products']
      .forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
  };

  const saveMutation = useMutation({
    mutationFn: (form) =>
      formModal?.mode === 'edit'
        ? updateSourcingOrder(formModal.data.id, buildSourcingPayload(form))
        : createSourcingOrder(buildSourcingPayload(form)),
    onSuccess: () => { invalidate(); setFormModal(null); setFormError(''); },
    onError: (err) => setFormError(err.response?.data?.message || 'Something went wrong.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteSourcingOrder(id),
    onSuccess: () => { invalidate(); setDeleteTarget(null); },
    onError: (err) => setDeleteError(err.response?.data?.message || 'Something went wrong.'),
  });

  const openDelete = (order) => { setDeleteError(''); setDeleteTarget(order); };

  const statusMutation = useMutation({
    mutationFn: ({ id, status }) => updateSourcingOrderStatus(id, status),
    onSuccess: (_, { id }) => { invalidate(); if (viewOrder?.id === id) setViewOrder(null); },
  });

  const openCreate = () => {
    setFormError('');
    const defaultSourcer = sourcers.find((s) => s.isDefault);
    setFormModal({ mode: 'create', initialForm: { ...EMPTY_FORM, sourcerId: defaultSourcer ? String(defaultSourcer.id) : '' } });
  };

  const openEdit = (order) => {
    setFormError('');
    const isRm = order.type === 'RAW_MATERIAL';
    setFormModal({
      mode: 'edit', data: order, received: isReceivedProduct(order),
      initialForm: {
        sourcerId: String(order.sourcerId),
        type: order.type ?? 'PRODUCT',
        notes: order.notes || '',
        manualDiscount: order.manualDiscount || 0,
        items: order.items.map((i) => {
          const totalCost = (Number(i.unitCost) * Number(i.quantity)).toFixed(2);
          return isRm
            ? { rawMaterialId: String(i.rawMaterialId), quantity: i.quantity, totalCost }
            : { productId: String(i.productId), quantity: i.quantity, totalCost, variantCombo: i.variantCombo || {} };
        }),
      },
    });
  };

  const orders = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;

  return (
    <div className={embedded ? '' : 'p-6 max-w-5xl'}>
      {embedded ? (
        <p className="text-sm text-gray-500 mb-3">{data?.totalElements ?? 0} {t('common.total')}</p>
      ) : (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{t('sourcingOrders.title')}</h1>
            <p className="text-sm text-gray-500 mt-0.5">{data?.totalElements ?? 0} {t('common.total')}</p>
          </div>
          {write && (
            <button onClick={openCreate} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
              <Plus size={15} /> {t('sourcingOrders.new')}
            </button>
          )}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="py-16 text-center text-sm text-gray-400">{t('common.loading')}</div>
        ) : orders.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">{t('sourcingOrders.noOrders')}</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left">
                <th className="px-4 py-3 font-medium text-gray-500">#</th>
                {!hideSourcer && <th className="px-4 py-3 font-medium text-gray-500">{t('sourcers.title')}</th>}
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.type')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.items')}</th>
                <th className="px-4 py-3 font-medium text-gray-500 text-right">{t('sourcingOrders.totalCost')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.status')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.date')}</th>
                <th className="px-4 py-3 w-24 font-medium text-gray-500">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {orders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-400 font-mono text-xs">#{order.id}</td>
                  {!hideSourcer && <td className="px-4 py-3 font-medium text-gray-800">{order.sourcerName}</td>}
                  <td className="px-4 py-3">{order.type && <TypeBadge type={order.type} />}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{order.items.length} item{order.items.length !== 1 ? 's' : ''}</td>
                  <td className="px-4 py-3 text-right font-medium text-gray-800">৳{fmt(order.totalCost)}</td>
                  <td className="px-4 py-3"><StatusBadge status={order.status} /></td>
                  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                    {formatDate(order.createdAt)}
                    {order.lastEditedAt && (
                      <span className="block text-amber-700" title={t('sourcingOrders.editedBy', { user: order.lastEditedBy, date: formatDate(order.lastEditedAt) })}>
                        {t('sourcingOrders.edited')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => setViewOrder(order)} className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors" title={t('orders.viewDetails')}>
                        <Eye size={14} />
                      </button>
                      {write && canEdit(order) && (
                        <button onClick={() => openEdit(order)} className="p-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                          title={isReceivedProduct(order) ? t('sourcingOrders.correctTitle', { id: order.id }) : undefined}>
                          <Pencil size={14} />
                        </button>
                      )}
                      {write && canDelete(order) && (
                        <button onClick={() => openDelete(order)} className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {viewOrder && (
        <DetailModal order={viewOrder} sourcers={sourcers} onClose={() => setViewOrder(null)}
          onStatusChange={(id, status) => statusMutation.mutate({ id, status })}
          isPending={statusMutation.isPending} hideSourcer={hideSourcer} />
      )}

      {formModal && (
        <SourcingOrderForm
          mode={formModal.mode}
          initialForm={formModal.initialForm ?? EMPTY_FORM}
          sourcers={sourcers}
          products={products}
          rawMaterials={rawMaterials}
          globalFields={globalFields}
          onSubmit={(form) => saveMutation.mutate(form)}
          onClose={() => { setFormModal(null); setFormError(''); }}
          isPending={saveMutation.isPending}
          error={formError}
          hideSourcer={hideSourcer}
          {...(formModal.received ? {
            lockType: true,
            title: t('sourcingOrders.correctTitle', { id: formModal.data.id }),
            submitLabel: t('sourcingOrders.saveCorrection'),
            notice: t('sourcingOrders.correctNotice'),
          } : {})}
        />
      )}

      {deleteTarget && (
        <Modal title={t('sourcingOrders.deleteOrder')} onClose={() => setDeleteTarget(null)}>
          <p className="text-sm text-gray-600"><span className="font-medium text-gray-900">#{deleteTarget.id}</span>? {t('common.cannotBeUndone')}</p>
          {isReceivedProduct(deleteTarget) && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
              {t('sourcingOrders.deleteReceivedWarning')}
            </p>
          )}
          {deleteError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-3">{deleteError}</p>
          )}
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">{t('common.cancel')}</button>
            <button onClick={() => deleteMutation.mutate(deleteTarget.id)} disabled={deleteMutation.isPending}
              className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg transition-colors">
              {deleteMutation.isPending ? t('common.deleting') : t('common.delete')}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
